import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import SideBarModal from "src/components/modals/SideBarModal";
import SuspenseLoader from "src/components/SuspenseLoader";
import { notifyError, notifySuccess } from "src/generic-ui";
import { usePermissionDetails } from "src/hooks/acl/permission/usePermissionDetails";
import { PermissionSchema, PermissionSchemaType } from "src/schemas/aclSchema";
import { logger } from "src/services";
import { permissionService } from "src/services/acl/permission.service";
import { IModals } from "src/types";
import { IPermission } from "src/types/acl";

interface ISidebar extends IModals {
  selectedId?: string;
  onRefresh?: () => void;
}
export default function Sidebar({
  isVisible,
  onClose,
  selectedId,
  onRefresh,
}: ISidebar) {
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const { isLoading, data, mutate, error, permissionTypes } = usePermissionDetails(selectedId);

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    errors: formikError,
    touched,
    setValues,
  } = useFormik({
    initialValues: {
      title: data?.title || "",
      isActive: data?.isActive || false,
      permissionType: data?.permissionTypeId || ""
    },
    validationSchema: PermissionSchema,
    enableReinitialize: true,
    onSubmit(values) {
      const onSave = !!selectedId ? onEditSubmit : onSaveSubmit;
      onSave(values);
    },
  });

  if (isLoading) {
    return <SuspenseLoader />;
  }

  const onSaveSubmit = async (values: PermissionSchemaType) => {
    try {
      setSubmitting(true);
      await permissionService.createPermission({ ...values });
      notifySuccess("permission created successfully")
      onRefresh?.();
      onClose();
    } catch (error) {
      logger.error(error);
      notifyError(error.message)
    } finally {
      setSubmitting(false);
    }
  };

  const onEditSubmit = async (values: PermissionSchemaType) => {
    try {
      setSubmitting(true);
      await permissionService.updatePermission(selectedId, { ...values });
      notifySuccess("permission updated successfully")
      onRefresh?.();
      onClose();
    } catch (error) {
      logger.error(error);
      notifyError(error.message)
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    try {
      setSubmitting(true);
      await permissionService.deletePermission(selectedId);
      notifySuccess("permission deleted successfully")
      onRefresh?.();
      onClose();
    } catch (error) {
      logger.error(error);
      notifyError(error.message)
    } finally {
      setSubmitting(false);
    }
  };

  const modifiedPermissionTypes = permissionTypes.filter((permissionType: any) => permissionType.title !== "*")

  return (
    <>
      <SideBarModal
        isVisible={isVisible}
        onClose={onClose}
        title={!selectedId ? "Add New" : "Edit Item"}
        renderEndIcon={
          !!selectedId && (
            <IconButton onClick={onDelete} disabled={isSubmitting}>
              <DeleteIcon className="text-red-400" />
            </IconButton>
          )
        }
      >
        <Box paddingX={2}>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <form onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Title"
                name="title"
                type="text"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {formikError.title && touched.title ? (
                <p className="text-red-600">{formikError.title}</p>
              ) : null}

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                fullWidth
                label="Select Parent Catalog"
                margin="dense"
                placeholder="Select Parent Catalog"
                onChange={handleChange}
                onBlur={handleBlur}
                name="permissionType"
                value={values.permissionType}
                className="mt-2"
              >
                {modifiedPermissionTypes?.map((item: IPermission, key: number) => {
                  return (
                    <MenuItem key={key} value={item?.id}>
                      {item?.title}
                    </MenuItem>
                  );
                })}
              </Select>
              {formikError.permissionType && touched.permissionType ? (
                <p className="text-red-600">{formikError.permissionType}</p>
              ) : null}

              <LoadingButton
                loadingIndicator="Loading..."
                type="submit"
                loading={isSubmitting}
                color="primary"
                className={"bg-indigo-600 hover:bg-indigo-800 mt-2 text-white"}
              >
                Submit
              </LoadingButton>
            </form>
          </Grid>
        </Box>
      </SideBarModal>
    </>
  );
}
